import React from 'react';
import './App.css';
import './PrintStyles.css';

// Auth Context
import { AuthContext, AuthProvider } from './contexts/AuthContext';
import { useAuth } from './hooks/useAuth';

// Extracted components - Pages
import LoginPageExternal from './pages/LoginPage';
import ResetPasswordPageExternal from './pages/ResetPasswordPage';
import EmailConfirmationPageExternal from './pages/EmailConfirmationPage';
import AdminDashboardExternal from './pages/AdminDashboard';
import ClientDashboardExternal from './pages/ClientDashboard';

// Error Boundary Component to catch rendering errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error:', error, errorInfo);
  }

  handleRetry = () => {
    // Clear potentially corrupted data
    localStorage.removeItem('user_data');
    localStorage.removeItem('access_token');
    // Reload the page
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#1A1E2E',
          color: '#FFFFFF',
          flexDirection: 'column',
          padding: '20px',
          textAlign: 'center'
        }}>
          <h1 style={{ fontSize: '24px', marginBottom: '16px', color: '#FF5757' }}>
            Une erreur est survenue
          </h1>
          <p style={{ color: '#8A92B2', marginBottom: '24px', maxWidth: '400px' }}>
            L'application a rencontré un problème. Cliquez sur le bouton ci-dessous pour réessayer.
          </p>
          <button
            onClick={this.handleRetry}
            style={{
              padding: '12px 24px',
              backgroundColor: '#00C2FF',
              color: '#1A1E2E',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Réessayer
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Main App Component
function App() {
  const { user, isLoading, isInitialized } = useAuth();

  // Check if we're on the password reset page
  const urlParams = new URLSearchParams(window.location.search);
  const resetToken = urlParams.get('token');
  const emailConfirmToken = urlParams.get('token');
  
  // Check if this is an email confirmation page
  if (window.location.pathname === '/confirm-email-change' && emailConfirmToken) {
    return <EmailConfirmationPageExternal token={emailConfirmToken} />;
  }

  // Check if this is a password reset page
  if (resetToken && window.location.search.includes('token=')) {
    return <ResetPasswordPageExternal token={resetToken} />;
  }

  // Show loading while verifying token
  if (isLoading || !isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#1A1E2E' }}>
        <div style={{ textAlign: 'center' }}>
          <div 
            className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-t-2" 
            style={{ borderColor: '#00C2FF' }}
          ></div>
          <p style={{ color: '#8A92B2', marginTop: '16px' }}>Vérification de la session...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginPageExternal AuthContext={AuthContext} />;
  }

  return (
    <div className="App">
      {user.role === 'admin' ? <AdminDashboardExternal /> : <ClientDashboardExternal />}
    </div>
  );
}

// Wrap App with AuthProvider and ErrorBoundary
function AppWithAuth() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <App />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default AppWithAuth;
